import React, { useCallback, useEffect, useRef } from 'react'

import AiSearchOverlay from '@disco/shared-components/search/ai-search-overlay/AiSearchOverlay'
import AiSearchToggle from '@disco/shared-components/search/ai-search-toggle/AiSearchToggle'
import { useAiSearchToggle } from '@disco/shared-components/search/ai-search-toggle/use-ai-search-toggle'
import {
  clearBrowseSearchTerm,
  getSearchResults,
  rerunBrowseSearch,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'
import {
  selectBrowseAiMode,
  selectBrowseLinkPreview,
  selectBrowseTypeAfterPromptSearch,
  setBrowseAiMode,
} from '@disco/state-management/reducers/search-page'
import { normalizeSearchTerm } from '@disco/utils/search'

import type { AiMode } from 'types/search-page'

import { Flex } from 'components/ds'

import { useDispatch, useSelector, useStore } from 'hooks/use-redux'

type Props = {
  // The input's live text; the store holds its normalized form.
  searchTerm: string
  // Empties the input's local text; the store term is cleared separately.
  clearInput: () => void
  // The input lives in the parent class component, so it's read lazily once mounted.
  getInput: () => HTMLElement | null | undefined
}

// Sits in the Browse search input's `after` slot; same toggle + prompt overlay as the /search page.
const BrowsePromptSearchToggle = ({
  searchTerm,
  clearInput,
  getInput,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const store = useStore()
  const browseAiMode = useSelector(selectBrowseAiMode)
  const linkPreview = useSelector(selectBrowseLinkPreview)
  const isLinkSearch = linkPreview?.url === normalizeSearchTerm(searchTerm)

  const setBrowseAiModeState = useCallback(
    (mode: AiMode) => dispatch(setBrowseAiMode(mode)),
    [dispatch],
  )
  // Read via ref so the input listeners below don't re-subscribe on every keystroke.
  const searchTermRef = useRef(searchTerm)
  searchTermRef.current = searchTerm

  // Undoes the Tracks lock, then reruns with the remaining filters or goes back to browse.
  // Not applyBrowseFilters: the filters are unchanged here, so it would skip the search.
  const clearSearch = useCallback(() => {
    clearInput()
    dispatch(clearBrowseSearchTerm())
    dispatch(rerunBrowseSearch())
  }, [clearInput, dispatch])

  // Closing without submitting goes back to this, so only a submitted prompt makes the mode 'active'.
  const modeBeforeOverlayRef = useRef<AiMode>('off')
  const handleCloseOverlay = useCallback(
    (prompt: string) => {
      setBrowseAiModeState(modeBeforeOverlayRef.current)
      // Emptying a submitted prompt clears the search; a keyword search is kept. Restored first so clearBrowseSearchTerm sees the prompt
      if (
        modeBeforeOverlayRef.current === 'active' &&
        !prompt.trim() &&
        searchTermRef.current.trim()
      ) {
        clearSearch()
      }
    },
    [setBrowseAiModeState, clearSearch],
  )
  const {
    aiMode,
    aiPrompt,
    setAiMode,
    setAiPrompt,
    clearPrompt,
    closeOverlay,
    rootRef,
  } = useAiSearchToggle(browseAiMode, setBrowseAiModeState, handleCloseOverlay)

  // Same as the /search page: a link search switches the toggle on.
  useEffect(() => {
    if (isLinkSearch && aiMode === 'off') {
      setAiMode('active')
    }
  }, [isLinkSearch, aiMode, setAiMode])

  const openOverlay = useCallback(() => {
    modeBeforeOverlayRef.current = selectBrowseAiMode(store.getState())
    const term = searchTermRef.current
    if (term.trim()) {
      setAiPrompt(term)
    }
    setAiMode('overlay')
  }, [store, setAiPrompt, setAiMode])

  // Same as the /search page: refocusing while prompt search is active reopens the overlay to edit the prompt.
  // Mode is read from the store at focus time: clicking from the overlay into the input closes it on mousedown, and focus lands before a re-render.
  useEffect(() => {
    const input = getInput()
    if (!input) return
    const handleFocus = () => {
      if (selectBrowseAiMode(store.getState()) === 'active') {
        openOverlay()
      }
    }
    // Same as the /search page: Tab from keyword mode opens the prompt overlay; Tab while it's open closes it and moves on.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const mode = selectBrowseAiMode(store.getState())
      if (mode === 'overlay') {
        closeOverlay()
      } else if (mode === 'off' && !e.shiftKey) {
        e.preventDefault()
        openOverlay()
      }
    }
    input.addEventListener('focus', handleFocus)
    input.addEventListener('keydown', handleKeyDown)
    return () => {
      input.removeEventListener('focus', handleFocus)
      input.removeEventListener('keydown', handleKeyDown)
    }
  }, [getInput, store, openOverlay, closeOverlay])

  // Tabbing past the prompt closes it; null relatedTarget is a click on plain content or a window switch.
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget
    if (
      !next ||
      e.currentTarget.contains(next) ||
      next === getInput() ||
      selectBrowseAiMode(store.getState()) !== 'overlay'
    ) {
      return
    }
    closeOverlay()
  }

  // Same behaviour as the /search page: turning the toggle off while typed text exists reruns a plain search with it.
  const handleDeactivate = () => {
    // Same as the /search page: a link always runs similarity search, so the toggle stays on.
    if (isLinkSearch) return
    setAiMode('off')
    clearPrompt()
    // Undo the Tracks lock handleAiSearch applied; only a submitted prompt ('active') set it. Track-only filters keep it.
    // Reopening the box over a submitted prompt makes the mode 'overlay', but the lock is still on.
    const wasPromptSearch =
      aiMode === 'active' ||
      (aiMode === 'overlay' && modeBeforeOverlayRef.current === 'active')
    dispatch(
      updateSearchParams(
        wasPromptSearch
          ? {
              type: selectBrowseTypeAfterPromptSearch(store.getState()),
              searchTerm,
            }
          : { searchTerm },
      ),
    )
    if (searchTerm.trim()) {
      dispatch(getSearchResults())
    }
  }

  const handleAiSearch = () => {
    if (!aiPrompt.trim()) {
      handleCloseOverlay(aiPrompt)
      return
    }
    dispatch(
      updateSearchParams({
        searchTerm: aiPrompt,
        searchOpen: true,
        type: 'track',
      }),
    )
    setAiMode('active')
    dispatch(getSearchResults())
    clearPrompt()
  }

  return (
    // Rendered inside the input's slot: Radix TextField.Root's pointerdown and SearchFilters' click both refocus the input, stealing focus from the prompt box.
    <Flex
      ref={rootRef}
      align="center"
      height="min-content"
      onPointerDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onBlur={handleBlur}
    >
      <AiSearchToggle
        active={aiMode !== 'off'}
        onActivate={openOverlay}
        onDeactivate={handleDeactivate}
      />
      {aiMode === 'overlay' && (
        <AiSearchOverlay
          value={aiPrompt}
          onChange={setAiPrompt}
          onClear={clearPrompt}
          onSubmit={handleAiSearch}
        />
      )}
    </Flex>
  )
}

export default BrowsePromptSearchToggle
